import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import * as _ from "lodash-es";
import momentTz from "moment-timezone";
import { useAppContextNonNull } from "@contexts/AppContext";
import { OwFnTable } from "../../shared/OwFnTable/OwFnTable";
import OwTitle from "../../shared/OwTitle/OwTitle";
import OwButton from "../../shared/OwButton/OwButton";
import OwLabel from "../../shared/OwLabel/OwLabel";
import OwToggle from "../../shared/OwToggle/OwToggle";
import { OwDeleteEditActions } from "../../shared/OwDeleteEditActions/OwDeleteEditActions";
import { ConfirmDeleteOverlayFn } from "../../shared/ConfirmDeleteOverlayFn/ConfirmDeleteOverlayFn";
import { ReportsNav } from "../ReportsNav/ReportsNav";
import { CreateReportSidebar } from "../CreateReportSidebar/CreateReportSidebar";
import IOwTableCols from "../../../models/IOwTableCols";
import IScheduled_Report from "../../../models/IScheduled_Report";
import ICustom_Report from "../../../models/ICustom_Report";
import ScheduledReportFrequencyEnum from "../../../models/ScheduledReportFrequencyEnum";
import ScheduledReportsService from "../../../services/ScheduledReportsService";
import UserService from "../../../services/UserService";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IGetCustomReportsDTO from "../../../models/DTO/IGetCustomReportsDTO";
import IOnGetCustomReportsDTO from "../../../models/DTO/IOnGetCustomReportsDTO";
import INotification from "../../../models/INotification";
import NotificationLevelEnum from "../../../models/NotificationLevelEnum";
import NotificationPositionEnum from "../../../models/NotificationPositionEnum";

type Props = {};

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const ordinal = (n: number): string => {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

export const ScheduledReports: React.FC<Props> = () => {
    const { userModel, supplierModel, socket, uiContext } = useAppContextNonNull();
    const intl = useIntl();

    const [schedules, setSchedules] = useState<IScheduled_Report[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [initialData, setInitialData] = useState(true);
    const [customReports, setCustomReports] = useState<ICustom_Report[]>([]);
    const [rowToDelete, setRowToDelete] = useState<IScheduled_Report | null>(null);
    const [deleteElemId, setDeleteElemId] = useState<string | null>(null);

    const notifyError = (message: string) => {
        uiContext.notifyFn({ eid: "", title: "error", message });
    };

    const notifySuccess = (message: string) => {
        const notification: INotification = {
            message,
            level: NotificationLevelEnum.success,
            position: NotificationPositionEnum.bc,
        };
        uiContext.notifyFn(null, notification);
    };

    const getSchedules = async () => {
        setIsLoading(true);
        try {
            const response = await ScheduledReportsService.getScheduledReports(userModel, supplierModel);
            setSchedules(response.schedules || []);
        } catch (err: any) {
            notifyError(err?.message || "error");
        } finally {
            setIsLoading(false);
            setInitialData(false);
        }
    };

    const getCustomReports = () => {
        const payload: IGetCustomReportsDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
        };
        socket.emit(EndpointsEnum.ReportGetCustomReports, payload);
    };

    const onGetCustomReports = (data: IOnGetCustomReportsDTO) => {
        setCustomReports(data.customReports);
    };

    useEffect(() => {
        document.title = `Orderwerks >> Scheduled Reports`;
        socket.on(EndpointsEnum.ReportOnGetCustomReports, onGetCustomReports);

        getSchedules();
        getCustomReports();

        return () => {
            socket.removeListener(EndpointsEnum.ReportOnGetCustomReports, onGetCustomReports);
        };
    }, []);

    const closeSidebar = (refresh?: boolean) => {
        uiContext.toggleSidebar(false);
        uiContext.setSidebarComponent(null);
        if (refresh) {
            getSchedules();
        }
    };

    const showSidebar = (row?: IScheduled_Report) => {
        const component = (
            <CreateReportSidebar
                close={closeSidebar}
                socket={socket}
                notifyFn={uiContext.notifyFn}
                setActivityIndicator={uiContext.setActivityIndicator}
                customReports={customReports}
                mode={"scheduled"}
                scheduledReport={row}
            />
        );
        uiContext.setSidebarComponent(component);
        uiContext.toggleSidebar(true);
    };

    const formatFrequency = (row: IScheduled_Report): string => {
        const timeText = row.time_of_day ? momentTz(row.time_of_day, "HH:mm").format("h:mm A") : "";

        switch (row.frequency) {
            case ScheduledReportFrequencyEnum.DAILY:
                return intl.formatMessage({ id: "scheduled_frequency_daily", defaultMessage: "Daily at {time}" }, { time: timeText });
            case ScheduledReportFrequencyEnum.WEEKLY: {
                const day = typeof row.day_of_week === "number" ? DAY_NAMES[row.day_of_week] : "";
                return intl.formatMessage(
                    { id: "scheduled_frequency_weekly", defaultMessage: "Weekly on {day}s at {time}" },
                    { day, time: timeText }
                );
            }
            case ScheduledReportFrequencyEnum.MONTHLY: {
                const day = typeof row.day_of_month === "number" ? ordinal(row.day_of_month) : "";
                return intl.formatMessage(
                    { id: "scheduled_frequency_monthly", defaultMessage: "Monthly on the {day} at {time}" },
                    { day, time: timeText }
                );
            }
            default:
                return "";
        }
    };

    const runNow = async (row: IScheduled_Report) => {
        try {
            await ScheduledReportsService.runNowScheduledReport(row.guid, userModel, supplierModel);
            notifySuccess(intl.formatMessage({ id: "scheduled_report_run_now_queued" }));
        } catch (err: any) {
            notifyError(err?.message || "error");
        }
    };

    const toggleActive = async (row: IScheduled_Report, active: boolean) => {
        try {
            await ScheduledReportsService.toggleScheduledReport(row.guid, active, userModel, supplierModel);
            getSchedules();
        } catch (err: any) {
            notifyError(err?.message || "error");
        }
    };

    const toggleConfirmDelete = (row: IScheduled_Report, index: number) => {
        setDeleteElemId(`row-${index}`);
        setRowToDelete(row);
    };

    const cancelDelete = () => {
        setRowToDelete(null);
        setDeleteElemId(null);
    };

    const commitDelete = async () => {
        if (!rowToDelete) return;
        try {
            await ScheduledReportsService.deleteScheduledReport(rowToDelete.guid, userModel, supplierModel);
            cancelDelete();
            getSchedules();
        } catch (err: any) {
            notifyError(err?.message || "error");
        }
    };

    const renderReportType = (row: IScheduled_Report) => {
        return _.startCase((row.report_type || "").replace(/_/g, " "));
    };

    const renderRecipients = (row: IScheduled_Report) => {
        return (row.recipient_emails || []).join(", ");
    };

    const renderDate = (dateStr: string | Date | null | undefined) => {
        if (!dateStr) return "—";
        return UserService.parseLastUpdated(String(dateStr), true);
    };

    const renderActive = (row: IScheduled_Report) => {
        return <OwToggle name={`active-${row.guid}`} label={""} active={!!row.active} fn={(e) => toggleActive(row, e.target.value)} />;
    };

    const renderActions = (row: IScheduled_Report, index: number) => {
        return (
            <div className={"d-flex align-items-center"}>
                <OwDeleteEditActions
                    isEditMode={false}
                    onTrashClickFn={() => toggleConfirmDelete(row, index)}
                    onEditClickFn={() => showSidebar(row)}
                    hideEdit={false}
                />
                <OwButton className={"btn-primary ms-2"} hoverTitle={"run_now"} centerIconClass={"fas fa-play"} fn={() => runNow(row)} />
            </div>
        );
    };

    const columns: IOwTableCols[] = [
        { title: "", valueProp: "", renderFn: renderActions, renderFnEmitsRow: true, isActionsCol: true },
        { title: "title", valueProp: "title" },
        { title: "report_type", valueProp: "report_type", renderFn: renderReportType, renderFnEmitsRow: true },
        { title: "frequency", valueProp: "", renderFn: formatFrequency, renderFnEmitsRow: true },
        { title: "recipient_emails", valueProp: "", renderFn: renderRecipients, renderFnEmitsRow: true },
        { title: "active", valueProp: "", renderFn: renderActive, renderFnEmitsRow: true },
        { title: "last_run", valueProp: "last_run_at", renderFn: renderDate },
        { title: "next_run", valueProp: "next_run_at", renderFn: renderDate },
    ];

    return (
        <div className={"ScheduledReports position-relative"}>
            <div className="mt-3 d-flex align-items-center">
                <OwTitle title="Reports" leftIconName="fad fa-chart-bar" />
                <div className={"ms-2"}>
                    <OwButton title={"create"} leftIconClass={"fas fa-plus"} fn={() => showSidebar()} className={"btn-warning"} />
                </div>
            </div>

            <ReportsNav />

            <div className={"text-end"}>
                <OwLabel title={"total_rows"} postText={":"} className={"me-2"} />
                {schedules.length}
            </div>

            <div className={"position-relative"}>
                <ConfirmDeleteOverlayFn
                    targetElem={deleteElemId}
                    handleConfirm={commitDelete}
                    handleCancel={cancelDelete}
                    nestedParentElem={"scheduled-reports"}
                    skipMainView={true}
                />

                <OwFnTable id={"scheduled-reports"} columns={columns} dataSet={schedules} dataFetch={isLoading} initialData={initialData} />
            </div>
        </div>
    );
};
